import { useEffect, useMemo, useState } from 'react';
import { nazivUloge, STATUSI, NASELJA } from './lib/konstante';
import { filtrirajZapise, poredajKucanstva } from './lib/evidencija';
import {
  odjaviSe,
  useSesija,
  useClanovi,
  useKucanstva,
  dohvatiKucanstvoZaUredjivanje,
  dohvatiKorisnickeUloge,
  spremiKorisnickuUlogu,
  useDokumenti,
  posaljiDokumente,
  poveznicaDokumenta,
  arhivirajDokument,
} from './hooks/usePodaci';
import Prijava from './components/Prijava';
import PopisEvidencije from './components/PopisEvidencije';
import KarticaClana from './components/KarticaClana';
import KarticaKucanstva from './components/KarticaKucanstva';
import Karta from './components/Karta';
import Ikona from './components/Ikona';
import Pregled, { Brojke } from './components/Pregled';
import UnosKucanstva from './components/UnosKucanstva';
import UvozKucanstava from './components/UvozKucanstava';
import PravaPristupa from './components/PravaPristupa';
import Dokumenti from './components/Dokumenti';

const PRAZNI_UVJETI = { upit: '', naselje: '', filter: 'svi' };
const bojaTocke = (t) => (STATUSI[t.status] ?? STATUSI.ne).boja;
const velicinaTocke = (t) => (t.broj > 1 ? 19 : 15);

function procitajPrikaz() {
  const upit = new URLSearchParams(window.location.search);
  const prikaz = upit.get('prikaz');
  return {
    stranica: [
      'popis',
      'karta',
      'unos',
      'uvoz',
      'pristup',
      'dokumenti',
    ].includes(prikaz)
      ? prikaz
      : upit.has('sloj')
        ? 'karta'
        : 'pregled',
    sloj: upit.get('sloj') === 'staro' ? 'staro' : 'novo',
    urediId: /^\d+$/.test(upit.get('uredi') ?? '')
      ? Number(upit.get('uredi'))
      : null,
  };
}

export default function App() {
  const {
    sesija,
    profil,
    jeUprava,
    ucitava: ucitavaSesija,
    greska: greskaSesije,
    osvjeziProfil,
  } = useSesija();
  const stari = useClanovi(sesija?.user.id);
  const novi = useKucanstva(sesija?.user.id);
  const [{ stranica, sloj, urediId }, postaviPrikaz] = useState(procitajPrikaz);
  const [uvjeti, postaviUvjete] = useState(PRAZNI_UVJETI);
  const [odabraniId, postaviOdabraniId] = useState(null);
  const [odjavaGreska, postaviOdjavaGreska] = useState(null);
  const [odjavljuje, postaviOdjavljuje] = useState(false);
  const [obavijest, postaviObavijest] = useState(null);
  const jePopisivac = jeUprava || profil?.uloga === 'popisivac';
  const dokumenti = useDokumenti(jePopisivac ? sesija?.user.id : null);
  const [dokumentZaId, postaviDokumentZaId] = useState(null);

  useEffect(() => {
    postaviOdabraniId(null);
    postaviUvjete(PRAZNI_UVJETI);
    postaviOdjavaGreska(null);
  }, [sesija?.user.id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [stranica, sloj]);

  const naPrikaz = (prikaz, izvor = sloj, filtri = null, uredjujeId = null) => {
    postaviPrikaz({ stranica: prikaz, sloj: izvor, urediId: uredjujeId });
    postaviObavijest(null);
    if (['unos', 'uvoz', 'pristup', 'dokumenti'].includes(prikaz))
      postaviOdabraniId(null);
    if (izvor !== sloj || prikaz === 'pregled' || filtri) {
      postaviOdabraniId(null);
      postaviUvjete({ ...PRAZNI_UVJETI, ...filtri });
    }
    const url = new URL(window.location.href);
    if (uredjujeId) url.searchParams.set('uredi', uredjujeId);
    else url.searchParams.delete('uredi');
    if (prikaz === 'pregled') {
      url.searchParams.delete('prikaz');
      url.searchParams.delete('sloj');
    } else {
      url.searchParams.set('prikaz', prikaz);
      url.searchParams.set('sloj', izvor);
    }
    window.history.pushState(
      null,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
  };

  useEffect(() => {
    const procitaj = () => {
      postaviPrikaz(procitajPrikaz());
      postaviOdabraniId(null);
      postaviUvjete(PRAZNI_UVJETI);
    };
    const tipka = (e) => {
      if (e.key === 'Escape') postaviOdabraniId(null);
    };
    window.addEventListener('popstate', procitaj);
    window.addEventListener('keydown', tipka);
    return () => {
      window.removeEventListener('popstate', procitaj);
      window.removeEventListener('keydown', tipka);
    };
  }, []);

  const novo = sloj === 'novo';
  const izvor = novo ? novi : stari;
  const zapisi = useMemo(
    () => (novo ? poredajKucanstva(novi.kucanstva) : stari.clanovi),
    [novo, novi.kucanstva, stari.clanovi],
  );
  const vidljivi = useMemo(
    () => filtrirajZapise(zapisi, uvjeti),
    [zapisi, uvjeti],
  );
  const odabrani = zapisi.find((z) => z.id === odabraniId);
  const tocke = useMemo(
    () =>
      vidljivi.map((z) => ({
        id: z.id,
        lat: z.lat,
        lng: z.lng,
        naslov: novo
          ? `${z.adresa} · ${z.nositelj_prezime} ${z.nositelj_ime}`
          : `${z.prezime} ${z.ime} · ${z.adresa ?? ''}`,
        status: z.status,
        broj: z.broj_prikljucaka,
      })),
    [vidljivi, novo],
  );
  const naUvjete = (noviUvjeti) => {
    postaviUvjete(noviUvjeti);
    postaviOdabraniId(null);
  };
  const naOdabirPregleda = (id) => {
    naPrikaz('popis', 'novo');
    postaviOdabraniId(id);
  };
  const naslov =
    stranica === 'dokumenti'
      ? 'Dokumenti'
      : stranica === 'unos'
        ? urediId
          ? 'Uredi kućanstvo'
          : 'Novo kućanstvo'
        : stranica === 'uvoz'
          ? 'Uvoz iz Excela'
          : stranica === 'pristup'
            ? 'Prava pristupa'
            : stranica === 'pregled'
              ? 'Pregled vodovoda'
              : stranica === 'karta'
                ? 'Karta priključaka'
                : novo
                  ? 'Kućanstva'
                  : 'Stari popis članova';
  const ucitava =
    stranica === 'pregled' ? stari.ucitava || novi.ucitava : izvor.ucitava;
  const greska =
    stranica === 'pregled' ? novi.greska || stari.greska : izvor.greska;
  const osvjezi = async () => {
    await Promise.all([stari.osvjezi(), novi.osvjezi()]);
  };
  const naOdjavu = async () => {
    postaviOdjavljuje(true);
    try {
      await odjaviSe();
    } catch (e) {
      postaviOdjavaGreska(e.message);
    } finally {
      postaviOdjavljuje(false);
    }
  };

  if (ucitavaSesija)
    return (
      <div className="cekanje cijeli-zaslon">
        <span className="ucitavanje" />
        <p>Otvaram radni prostor…</p>
      </div>
    );
  if (!sesija) return <Prijava />;
  if (greskaSesije || !profil)
    return (
      <div className="cekanje cijeli-zaslon">
        <Ikona naziv="lokot" velicina={36} />
        <h2>Profil nije dostupan</h2>
        <p>
          {greskaSesije ||
            'Uprava treba povezati račun s korisničkim profilom.'}
        </p>
        <button className="gumb" onClick={osvjeziProfil}>
          Pokušaj ponovno
        </button>
        <button
          className="tekst-gumb"
          onClick={() =>
            odjaviSe().catch((e) => postaviOdjavaGreska(e.message))
          }
        >
          Odjavi se
        </button>
        {odjavaGreska && <p role="alert">{odjavaGreska}</p>}
      </div>
    );

  const naNovo = () => naPrikaz('unos', 'novo');
  const naPopis = () => naPrikaz('popis', 'novo', {});
  const naPostojece = (id) => {
    naPopis();
    postaviOdabraniId(id);
  };
  const naSpremljeno = (rezultat) => {
    dokumenti.osvjezi();
    naPostojece(rezultat.id);
    postaviObavijest(
      rezultat.osvjezeno
        ? rezultat.ponovljeno
          ? 'Ovo kućanstvo već je spremljeno istim zahtjevom.'
          : 'Kućanstvo i kontakti su spremljeni.'
        : 'Kućanstvo je spremljeno. Osvježi prikaz za nove podatke.',
    );
  };
  const navigacija = [
    {
      naziv: 'Pregled',
      ikona: 'pregled',
      aktivno: stranica === 'pregled',
      radnja: () => naPrikaz('pregled'),
    },
    {
      naziv: 'Kućanstva',
      ikona: 'kuca',
      aktivno: stranica === 'popis' && novo,
      radnja: () => naPrikaz('popis', 'novo'),
      broj: novi.greska || novi.ucitava ? null : novi.kucanstva.length,
    },
    {
      naziv: 'Karta priključaka',
      ikona: 'karta',
      aktivno: stranica === 'karta',
      radnja: () => naPrikaz('karta'),
    },
    {
      naziv: 'Stari popis',
      ikona: 'knjiga',
      aktivno: stranica === 'popis' && !novo,
      radnja: () => naPrikaz('popis', 'staro'),
    },
  ];
  const naDokumente = (id = null) => {
    postaviDokumentZaId(id);
    naPrikaz('dokumenti', 'novo');
  };
  if (jePopisivac)
    navigacija.push({
      naziv: 'Dokumenti',
      ikona: 'dokument',
      aktivno: stranica === 'dokumenti',
      radnja: () => naDokumente(),
    });
  if (jeUprava)
    navigacija.push({
      naziv: 'Uvoz iz Excela',
      ikona: 'popis',
      aktivno: stranica === 'uvoz',
      radnja: () => naPrikaz('uvoz', 'novo'),
    });
  if (profil.uloga === 'superadmin')
    navigacija.push({
      naziv: 'Prava pristupa',
      ikona: 'korisnik',
      aktivno: stranica === 'pristup',
      radnja: () => naPrikaz('pristup', 'novo'),
    });

  return (
    <div className="ljuska">
      <a className="preskoci" href="#glavni-sadrzaj">
        Preskoči na sadržaj
      </a>
      <aside className="bocna-traka">
        <button
          className="marka"
          onClick={() => naPrikaz('pregled')}
          aria-label="Vodovod Jakšinec, pregled"
        >
          <span className="znak-vodovoda">
            <Ikona naziv="kap" velicina={29} />
          </span>
          <span>
            vodovod
            <strong>
              Jakšinec<span>.</span>
            </strong>
          </span>
        </button>
        <span className="oznaka-radnog-prostora">EVIDENCIJA VODOVODA</span>
        <nav
          aria-label="Glavna navigacija"
          style={{ '--broj-izbornika': navigacija.length }}
        >
          {navigacija.map((n) => (
            <button
              key={n.naziv}
              aria-label={n.naziv}
              title={n.naziv}
              className={n.aktivno ? 'aktivno' : ''}
              aria-current={n.aktivno ? 'page' : undefined}
              onClick={n.radnja}
            >
              <Ikona naziv={n.ikona} />
              <span>{n.naziv}</span>
              {n.broj != null && <b>{n.broj}</b>}
            </button>
          ))}
        </nav>
        <div className="zajednica">
          <span className="nadnaslov">NAŠA ZAJEDNICA</span>
          {NASELJA.map((n) => (
            <div key={n.naziv}>
              <i />
              {n.naziv}
            </div>
          ))}
          <p>
            Privatni vodovod
            <br />
            Općina Gornja Stubica
          </p>
        </div>
        <div className="profil">
          <span className="avatar-profila">
            {(profil.korisnicko_ime || profil.ime || 'K')[0].toUpperCase()}
          </span>
          <span>
            <strong>{profil.korisnicko_ime || profil.ime}</strong>
            <small>{nazivUloge(profil.uloga)}</small>
          </span>
          <button
            aria-label="Odjavi se"
            title="Odjavi se"
            disabled={odjavljuje}
            onClick={naOdjavu}
          >
            <Ikona naziv="odjava" velicina={19} />
          </button>
        </div>
      </aside>
      <div className="glavni-okvir">
        <header className="vrh">
          <div className="putanja">
            Radni prostor <Ikona naziv="desno" velicina={13} />
            <span>{stranica === 'pregled' ? 'Pregled' : naslov}</span>
          </div>
          <div className="alati">
            <span className="privatna-evidencija">
              <Ikona naziv="lokot" velicina={13} /> Privatna evidencija
            </span>
            <button
              className="gumb-ikona"
              aria-label="Osvježi podatke"
              title="Osvježi podatke"
              disabled={ucitava}
              onClick={osvjezi}
            >
              <Ikona naziv="osvjezi" velicina={18} />
            </button>
            <button
              className="gumb-ikona mobilna-odjava"
              aria-label="Odjavi se"
              title="Odjavi se"
              disabled={odjavljuje}
              onClick={naOdjavu}
            >
              <Ikona naziv="odjava" velicina={18} />
            </button>
          </div>
        </header>
        <main
          id="glavni-sadrzaj"
          className={`glavni-sadrzaj ${stranica === 'karta' ? 'prikaz-karte' : ''}`}
        >
          <div className="naslov-stranice">
            <div>
              <span className="nadnaslov">VODOVOD JAKŠINEC</span>
              <h1>{naslov}</h1>
              <p>
                {stranica === 'dokumenti'
                  ? 'Privatni dokumenti vodovoda i potpisane privole kućanstava.'
                  : stranica === 'unos'
                    ? 'Upiši nositelja, adresu i kontakte kućanstva.'
                    : stranica === 'uvoz'
                      ? 'Prepoznaj stupce, pregledaj nejasnoće i spremi samo odabrane zapise.'
                      : stranica === 'pristup'
                        ? 'Upravljaj ulogama računa za prijavu u aplikaciju.'
                        : stranica === 'pregled'
                          ? 'Dobar pregled počinje pouzdanim podacima.'
                          : stranica === 'karta'
                            ? 'Pronađi priključak i provjeri njegovu poziciju.'
                            : novo
                              ? 'Nova evidencija nositelja, priključaka i lokacija.'
                              : 'Povijesna evidencija prepisana iz izvorne bilježnice.'}
              </p>
            </div>
            {['unos', 'uvoz', 'pristup', 'dokumenti'].includes(stranica) ? (
              <button className="gumb sek" onClick={naPopis}>
                Natrag na kućanstva
              </button>
            ) : stranica === 'pregled' ? (
              <span className="datum-pregleda">
                {new Intl.DateTimeFormat('hr-HR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  timeZone: 'Europe/Zagreb',
                }).format(new Date())}
              </span>
            ) : (
              <div className="radnje-naslova">
                {jePopisivac && novo && (
                  <button className="gumb" onClick={naNovo}>
                    + Novo kućanstvo
                  </button>
                )}
                {jeUprava && novo && (
                  <button
                    className="gumb sek"
                    onClick={() => naPrikaz('uvoz', 'novo')}
                  >
                    Uvezi Excel
                  </button>
                )}
                <div className="odabir-prikaza">
                  <button
                    aria-pressed={stranica === 'popis'}
                    onClick={() => naPrikaz('popis')}
                  >
                    <Ikona naziv="popis" velicina={17} />
                    Popis
                  </button>
                  <button
                    aria-pressed={stranica === 'karta'}
                    onClick={() => naPrikaz('karta')}
                  >
                    <Ikona naziv="karta" velicina={17} />
                    Karta
                  </button>
                </div>
              </div>
            )}
          </div>
          {odjavaGreska && (
            <p className="obavijest greska" role="alert">
              {odjavaGreska}
            </p>
          )}
          {['unos', 'uvoz'].includes(stranica) && novi.greska && (
            <div className="obavijest greska" role="alert">
              {novi.greska} Spremanje je zaustavljeno dok se evidencija ne
              učita.
              <button className="tekst-gumb" onClick={novi.osvjezi}>
                Ponovno učitaj evidenciju
              </button>
            </div>
          )}
          {obavijest && (
            <p className="obavijest ok" role="status">
              {obavijest}
              <button
                className="tekst-gumb"
                onClick={() => postaviObavijest(null)}
              >
                U redu
              </button>
            </p>
          )}
          {stranica === 'dokumenti' ? (
            jePopisivac ? (
              <Dokumenti
                key={`${sesija.user.id}-${dokumentZaId ?? 'svi'}`}
                stanje={dokumenti}
                kucanstva={novi.kucanstva}
                pocetnoKucanstvoId={dokumentZaId}
                jeUprava={jeUprava}
                korisnikId={sesija.user.id}
                naPosalji={posaljiDokumente}
                naPreuzmi={poveznicaDokumenta}
                naArhiviraj={arhivirajDokument}
                naNovo={naNovo}
              />
            ) : (
              <p className="obavijest greska">
                Dokumenti su dostupni popisivačima i upravi prema ovlastima.
              </p>
            )
          ) : stranica === 'unos' ? (
            jePopisivac ? (
              <UnosKucanstva
                key={`${sesija.user.id}-${urediId ?? 'novo'}`}
                korisnikId={sesija.user.id}
                jeUprava={jeUprava}
                urediId={urediId}
                kucanstva={novi.kucanstva}
                ucitavaEvidenciju={novi.ucitava || !!novi.greska}
                naDohvat={dohvatiKucanstvoZaUredjivanje}
                naSpremi={novi.spremiKucanstvo}
                naPrivole={posaljiDokumente}
                naSpremljeno={naSpremljeno}
                naOdustani={naPopis}
                naPostojece={naPostojece}
              />
            ) : (
              <p className="obavijest greska">
                Unos kućanstava dostupan je popisivačima i upravi.
              </p>
            )
          ) : stranica === 'uvoz' ? (
            jeUprava ? (
              <UvozKucanstava
                kucanstva={novi.kucanstva}
                ucitavaEvidenciju={novi.ucitava || !!novi.greska}
                naUvoz={novi.uveziKucanstva}
                naPopis={naPopis}
                naPostojece={postaviOdabraniId}
              />
            ) : (
              <p className="obavijest greska">
                Uvoz iz Excela dostupan je upravi.
              </p>
            )
          ) : stranica === 'pristup' ? (
            profil.uloga === 'superadmin' ? (
              <PravaPristupa
                korisnikId={sesija.user.id}
                naDohvat={dohvatiKorisnickeUloge}
                naSpremi={spremiKorisnickuUlogu}
              />
            ) : (
              <p className="obavijest greska">
                Uloge mijenja samo super admin.
              </p>
            )
          ) : ucitava ? (
            <div className="cekanje">
              <span className="ucitavanje" />
              <p>Učitavam evidenciju…</p>
            </div>
          ) : greska ? (
            <div className="cekanje">
              <span className="ikona-ploha jantar">
                <Ikona naziv="info" />
              </span>
              <h2>Podaci trenutačno nisu dostupni</h2>
              <p>{greska}</p>
              <button className="gumb" onClick={osvjezi}>
                <Ikona naziv="osvjezi" velicina={17} /> Pokušaj ponovno
              </button>
            </div>
          ) : stranica === 'pregled' ? (
            <Pregled
              kucanstva={novi.kucanstva}
              clanovi={stari.clanovi}
              naPrikaz={naPrikaz}
              naOdabir={naOdabirPregleda}
              naNovo={jePopisivac ? naNovo : null}
            />
          ) : (
            <>
              {stranica === 'popis' && <Brojke zapisi={zapisi} novo={novo} />}
              <div className="traka-slojeva">
                <div className="slojevi" aria-label="Izvor evidencije">
                  <button
                    aria-pressed={novo}
                    onClick={() => naPrikaz(stranica, 'novo')}
                  >
                    <span className="tockica" />
                    Novo stanje
                  </button>
                  <button
                    aria-pressed={!novo}
                    onClick={() => naPrikaz(stranica, 'staro')}
                  >
                    Stara bilježnica
                  </button>
                </div>
                <span className="opis-sloja">
                  {novo
                    ? 'Jedna adresa · jedno kućanstvo'
                    : 'Izvorni zapisi · zasebna evidencija'}
                </span>
              </div>
              <div
                className={
                  stranica === 'karta'
                    ? 'radni-prostor-karte'
                    : 'radni-prostor-popisa'
                }
              >
                <PopisEvidencije
                  zapisi={vidljivi}
                  sviZapisi={zapisi}
                  novo={novo}
                  odabraniId={odabraniId}
                  naOdabir={postaviOdabraniId}
                  uvjeti={uvjeti}
                  naUvjete={naUvjete}
                  naKartu={() => naPrikaz('karta')}
                  sazet={stranica === 'karta'}
                />
                {stranica === 'karta' && (
                  <div className="radni-dio">
                    <Karta
                      tocke={tocke}
                      odabraniId={odabraniId}
                      naOdabir={postaviOdabraniId}
                      bojaTocke={bojaTocke}
                      velicinaTocke={velicinaTocke}
                    />
                    <div className="legenda-karte">
                      {Object.entries(STATUSI)
                        .filter(([kljuc]) => kljuc !== 'ne')
                        .map(([kljuc, s]) => (
                          <span key={kljuc}>
                            <i style={{ background: s.boja }} />
                            {s.naziv}
                          </span>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          <footer className="podnozje-aplikacije">
            <span>
              Vodovod Jakšinec <i /> Evidencija naše zajednice
            </span>
            <span>
              <Ikona naziv="lokot" velicina={12} /> Pristup samo ovlaštenim
              korisnicima
            </span>
          </footer>
        </main>
      </div>
      {odabrani && (
        <div
          className={`okvir-detalja ${stranica === 'karta' ? 'detalji-karte' : ''}`}
        >
          {novo ? (
            <KarticaKucanstva
              key={`novo-${odabrani.id}`}
              kucanstvo={odabrani}
              mozeUredjivati={jePopisivac}
              naUredjivanje={() => naPrikaz('unos', 'novo', null, odabrani.id)}
              dokumenti={dokumenti.dokumenti.filter(
                (d) =>
                  d.kucanstvo_id === odabrani.id &&
                  !d.arhivirano &&
                  d.stanje === 'spremno',
              )}
              dokumentiGreska={dokumenti.greska}
              ucitavaDokumente={dokumenti.ucitava}
              naDokumente={() => naDokumente(odabrani.id)}
              jeUprava={jeUprava}
              onSpremi={novi.spremiTocku}
              onPotvrdi={novi.potvrdiTocku}
              onUkloni={novi.ukloniTocku}
              onZatvori={() => postaviOdabraniId(null)}
            />
          ) : (
            <KarticaClana
              key={`staro-${odabrani.id}`}
              clan={odabrani}
              jeUprava={jeUprava}
              onSpremi={stari.spremiTocku}
              onPotvrdi={stari.potvrdiTocku}
              onUkloni={stari.ukloniTocku}
              onZatvori={() => postaviOdabraniId(null)}
            />
          )}
        </div>
      )}
    </div>
  );
}
